import React, { useCallback, useContext, useMemo } from 'react'
import { GoogleOneTap as GoogleOneTapForm, Auth } from '@grammarly/identity-ui'

import { useBrowserService } from '../../lib/hooks'
import { navHelper } from '../../lib/services/navigation'
import { BrowserService } from '../../lib/services/browser'
import { goToEditor } from '../../lib/utils/navigate'
import { useIsEligibleForLlama } from '../../lib/hooks/install-eligibility'
import { PageContext } from '../../contexts/page-context'
import { ExternalProps, withDefaultAuthProps } from '../unified-auth/with-authentication'
import { GoogleOneTapTracker } from './tracking'

import '@grammarly/identity-ui/dist/prebuilt/esm/index.css'

const GoogleOneTapWithDefaultAuthProps = withDefaultAuthProps<Auth.Props.GoogleOneTap>(GoogleOneTapForm)

interface GoogleOneTapDefaultProps {
  autoSignIn?: boolean
  fixedPositionDesktop?: Auth.Props.GoogleOneTap['fixedPositionDesktop']
  onAfterAuthReject?: ExternalProps['onAfterAuthReject']
  onRedirectAfterAuthSuccess?: ExternalProps['onRedirectAfterAuthSuccess']
}

function isLlamaAppForCurrentOsInstalled(user: Auth.User, browserService: BrowserService): boolean {
  const llamaWindowsGroup = 'windowsExtension'
  const llamaMacGroup = 'macExtension'

  const { isWindows, isMac, isIOS } = browserService
  const targetGroup = isWindows ? llamaWindowsGroup : isMac && !isIOS ? llamaMacGroup : undefined

  return !!targetGroup && user.groups.includes(targetGroup)
}

const GoogleOneTapDefault: React.FC<GoogleOneTapDefaultProps> = (props) => {
  const browserService = useBrowserService()
  const { pageId, slug } = useContext(PageContext)
  const isEligibleLlama = useIsEligibleForLlama()

  const googleOneTapTracker: Auth.Tracking.GoogleOneTap.Tracker = useMemo(
    () => new GoogleOneTapTracker(pageId, slug),
    [pageId, slug]
  )

  const onRedirectAfterGoogleOneAuthSuccess = useCallback(
    (authContext: Auth.Context, authUser: Auth.User, defaultRedirect: () => void) => {
      // If automatic sign-in and not on auth pages, stay on current page
      if (props.autoSignIn) {
        const currentSlug = slug
        const isAuthPage = currentSlug === '/'

        if (!isAuthPage) {
          // Stay on current page - do nothing
          return Promise.resolve()
        }
      }

      // For SSO sign-in just go to the editor
      if (Auth.isAuthSSOContext(authContext) && authContext.action === 'SIGNIN') {
        return goToEditor()
      }

      // All sign-ups to the recommendation flow
      if (authContext.action === 'SIGNUP') {
        return navHelper.navigateFunnelPath('/personalize', navHelper.currentQueryParams)
      }

      // If the device is Llama-eligible and no Llama installed -- go to the download page
      // go to product/recommendation page in experiment instead.
      if (isEligibleLlama && !isLlamaAppForCurrentOsInstalled(authUser, browserService)) {
        return navHelper.navigateFunnelPath('/product/recommendation')
      }

      return defaultRedirect()
    },
    [browserService, isEligibleLlama, props.autoSignIn, slug]
  )

  return (
    <GoogleOneTapWithDefaultAuthProps
      autoSignIn={props.autoSignIn}
      googleOneTapTracker={googleOneTapTracker}
      onRedirectAfterAuthSuccess={onRedirectAfterGoogleOneAuthSuccess}
      {...props}
    />
  )
}

export default GoogleOneTapDefault
